import {
  AttendanceReportFragment,
  BasicProfileFragment,
} from "./Attendance-generated.hooks";
import { AttendanceFilterValue } from "./AttendanceFilter";
import { BadgeColor } from "@pushpress/shared-components";
import type { AttendanceStatus } from "../../../graphql/graphql-generated.types";
import { createCsvContent } from "../../../utils/csvUtils";
import { formatInTimeZone } from "date-fns-tz";
import { joinStrings } from "../../../utils/misc";

export type AttendanceStatusOption = { value: AttendanceStatus; label: string };

export const attendanceStatusLabel: { [key in AttendanceStatus]: string } = {
  CANCELLED: "Cancelled",
  CHECKED_IN: "Checked In",
  LATE_CANCEL: "Late Cancel",
  NO_SHOW: "No Show",
  RESERVED: "Reserved",
  WAIT_LIST: "Wait List",
  CHECK_IN_FAILED: "Check-In Failed",
};

export const attendanceStatusColor: { [key in AttendanceStatus]: BadgeColor } =
  {
    CANCELLED: BadgeColor.Error,
    CHECKED_IN: BadgeColor.Success,
    LATE_CANCEL: BadgeColor.Error,
    NO_SHOW: BadgeColor.Error,
    RESERVED: BadgeColor.Info,
    WAIT_LIST: BadgeColor.Warning,
    CHECK_IN_FAILED: BadgeColor.Error,
  };

export const attendanceStatusOptions: AttendanceStatusOption[] = [
  { value: "CHECKED_IN", label: "Checked In" },
  { value: "CHECK_IN_FAILED", label: "Check-In Failed" },
  { value: "LATE_CANCEL", label: "Late Cancel" },
  { value: "NO_SHOW", label: "No Show" },
  { value: "RESERVED", label: "Reserved" },
  { value: "WAIT_LIST", label: "Wait List" },
];

export function filterAttendanceReport(
  data: AttendanceReportFragment[],
  filter?: AttendanceFilterValue,
): AttendanceReportFragment[] {
  return data.filter((item) => {
    // filter by status
    const itemHasStatus = filter?.status?.some((f) => f.value === item.status);
    if (filter?.status?.length && !itemHasStatus) {
      return false;
    }

    // filter by plan
    const itemHasSubscription = filter?.subscriptions?.some(
      (f) => f.subscriptionUuid === item.subscription?.subscriptionUuid,
    );
    if (filter?.subscriptions?.length && !itemHasSubscription) {
      return false;
    }

    return true;
  });
}

export const getAttendanceReportFilterTagsMap = (
  filter: AttendanceFilterValue,
) => {
  const result = {};

  // tags by status
  const status = filter?.status || [];
  status.forEach((statusItem) => {
    const key = `status-${statusItem.value}`;
    const label = statusItem.label;
    result[key] = {
      label,
      onDelete: () => ({
        ...filter,
        status: filter?.status?.filter(
          (item) => item.value !== statusItem.value,
        ),
      }),
    };
  });

  // tag by plan
  const subscriptions = filter?.subscriptions || [];
  subscriptions.forEach((subItem) => {
    const key = `sub-${subItem.planObject.uuid}`;
    const label = subItem.planObject.name;
    result[key] = {
      label,
      onDelete: () => ({
        ...filter,
        subscriptions: filter?.subscriptions?.filter(
          (item) => item.planObject.uuid !== subItem.planObject.uuid,
        ),
      }),
    };
  });

  return result;
};

const ATTENDANCE_CSV_HEADER_MAP = {
  id: "id",
  type: "type",
  customer: "customer",
  class: "class",
  classDateTime: "class_date_time",
  reservationTime: "reservation_time",
  checkinTime: "checkin_time",
  status: "status",
};

export function createAttendanceReportCsv(
  data: AttendanceReportFragment[],
  profile: BasicProfileFragment,
  timezone: string,
) {
  if (!data) {
    return "Empty content";
  }
  const rows = data.map((item) => {
    const checkin = item.checkin || item.registration?.checkin;
    const registration = item.registration;
    const calendarItem = item.calendarItem;
    return {
      id: checkin?.uuid ?? registration?.uuid,
      type: checkin?.uuid ? "checkin" : "registration",
      customer: joinStrings(profile.firstName, profile.lastName),
      class: calendarItem?.title || "--",
      classDateTime: calendarItem
        ? formatCsvDateString(calendarItem?.startDatetime, timezone)
        : "--",
      reservationTime: formatCsvDate(
        registration?.registrationTimestamp,
        timezone,
      ),
      checkinTime: formatCsvDate(checkin?.checkinTimestamp, timezone),
      status: item.status,
    };
  });
  return createCsvContent(rows, ATTENDANCE_CSV_HEADER_MAP);
}

function formatCsvDate(timestamp: number, timezone: string) {
  return timestamp
    ? formatInTimeZone(timestamp * 1000, timezone, "MM-dd-yyyy h:mm a")
    : "--";
}

function formatCsvDateString(date: string, timezone: string) {
  return date
    ? formatInTimeZone(new Date(date), timezone, "MM-dd-yyyy h:mm a")
    : "--";
}
